import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Trash2, Loader2, ArrowLeft } from 'lucide-react';

interface DeleteRaceModalProps {
    raceName: string;
    runnerCount: number;
    isDeleting: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

export function DeleteRaceModal({ raceName, runnerCount, isDeleting, onConfirm, onCancel }: DeleteRaceModalProps) {
    const [step, setStep] = useState<1 | 2>(1);
    const [confirmInput, setConfirmInput] = useState('');
    const { t } = useTranslation('races');
    const { t: tc } = useTranslation();

    const canConfirm = confirmInput === raceName;

    return createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-200"
                onClick={onCancel}
            />

            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm relative z-10 p-6 animate-in zoom-in-95 duration-200 border border-slate-100">
                <div className="flex flex-col items-center text-center gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-2 bg-red-50 text-red-500">
                        <Trash2 size={24} />
                    </div>

                    {step === 1 ? (
                        <>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">{t('details.deleteRaceModal.title')}</h3>
                                <p className="text-sm text-slate-500 mt-2">
                                    <Trans
                                        i18nKey="races:details.deleteRaceModal.warning"
                                        values={{ name: raceName, runnerCount }}
                                        components={{ strong: <span className="font-bold text-slate-800" /> }}
                                    />
                                </p>
                            </div>

                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={onCancel}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95"
                                >
                                    {tc('btn.cancel')}
                                </button>
                                <button
                                    onClick={() => setStep(2)}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-colors active:scale-95"
                                >
                                    {t('details.deleteRaceModal.continue')}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="w-full">
                                <h3 className="text-lg font-bold text-slate-900">{t('details.deleteRaceModal.confirmTitle')}</h3>
                                <input
                                    type="text"
                                    value={confirmInput}
                                    onChange={(e) => setConfirmInput(e.target.value)}
                                    placeholder={t('details.deleteRaceModal.confirmPlaceholder')}
                                    className="w-full mt-3 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300"
                                    autoFocus
                                />
                                <p className="text-xs text-slate-400 mt-2 select-all font-mono">{raceName}</p>
                            </div>

                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    onClick={() => { setStep(1); setConfirmInput(''); }}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <ArrowLeft size={16} />
                                    {t('details.deleteRaceModal.back')}
                                </button>
                                <button
                                    onClick={onConfirm}
                                    disabled={!canConfirm || isDeleting}
                                    className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-colors active:scale-95 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {isDeleting ? <Loader2 size={18} className="animate-spin" /> : tc('btn.delete')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
